import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdAutoAwesome, MdAutorenew } from 'react-icons/md';
import MainURL from '../../../../MainURL';
import { themesList } from '../../../../DefaultData';
import { useContiAi } from './useContiAi';
import './ContiMaker.scss';
import altImage from '../../../../images/altImage.jpeg';

interface SongData {
  id: number;
  title: string;
  stateSort: string;
  keySort: string;
  theme: string;
  tempoSort: string;
  image: string;
  lyrics: string;
}

interface SongCondition {
  stateSort: string;
  theme: string;
  tempoSort: string;
}

export default function ContiMaker() {
  const navigate = useNavigate();
  const contiAi = useContiAi();

  const [allSongs, setAllSongs] = useState<SongData[]>([]);
  const [filteredSongs, setFilteredSongs] = useState<SongData[]>([]);
  const [selectedSongs, setSelectedSongs] = useState<SongData[]>([]);
  
  // 필터 조건
  const [selectedStateSort, setSelectedStateSort] = useState<string>('');
  const [selectedKeySort, setSelectedKeySort] = useState<string>('');
  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const [selectedTempoSort, setSelectedTempoSort] = useState<string>('');
  const [songCount, setSongCount] = useState<number>(5);
  
  // 단계별 선택을 위한 상태
  const [step, setStep] = useState<number>(1); // 1: 곡수 선택, 2: 세부 조건 선택
  const [songConditions, setSongConditions] = useState<SongCondition[]>([]);
  
  // 드롭다운 옵션들
  const [stateSortOptions, setStateSortOptions] = useState<string[]>([]);
  const [themeOptions, setThemeOptions] = useState<string[]>([]);
  const [tempoSortOptions, setTempoSortOptions] = useState<string[]>([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [songsPerRow, setSongsPerRow] = useState<number>(3); // 한 줄에 표시할 곡 개수
  const [paperSize, setPaperSize] = useState<string>('A4 가로'); // 용지 크기
  const contiGridRef = useRef<HTMLDivElement>(null); // 캡처할 영역 ref
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  // 용지 크기에 따른 width/height 계산 (mm to px, 96 DPI 기준)
  const getPaperDimensions = (size: string): { width: string; height: string } => {
    const mmToPx = (mm: number) => `${(mm * 96) / 25.4}px`;
    switch (size) {
      case 'A4 가로': return { width: mmToPx(297), height: mmToPx(210) }; // 가로 297mm × 세로 210mm
      case 'A4 세로': return { width: mmToPx(210), height: mmToPx(297) }; // 가로 210mm × 세로 297mm
      default: return { width: mmToPx(297), height: mmToPx(210) };
    }
  };

  // 모든 곡 불러오기
  useEffect(() => {
    fetchAllSongs();
  }, []);

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAllSongs = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${MainURL}/worshipsongs/getsongsall`);
      if (response.data) {
        const songs = response.data.result;
        setAllSongs(songs);
        console.log(songs);
        // 드롭다운 옵션들 추출
        const states = Array.from(new Set(songs.map((song: SongData) => song.stateSort).filter(Boolean))) as string[];
        const tempos = Array.from(new Set(songs.map((song: SongData) => song.tempoSort).filter(Boolean))) as string[];

        setStateSortOptions(states.sort());
        setThemeOptions(themesList);
        console.log(themesList);
        setTempoSortOptions(tempos.sort());
      }
    } catch (error) {
      console.error('곡을 불러오는 중 오류가 발생했습니다:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 필터 조건에 따라 곡 필터링
  const filterSongs = () => {
    let filtered = allSongs;

    if (selectedStateSort) {
      filtered = filtered.filter(song => song.stateSort === selectedStateSort);
    }
    if (selectedKeySort) {
      filtered = filtered.filter(song => song.keySort === selectedKeySort);
    }
    if (selectedTheme) {
      filtered = filtered.filter(song => song.theme && song.theme.includes(selectedTheme));
    }
    if (selectedTempoSort) {
      filtered = filtered.filter(song => song.tempoSort === selectedTempoSort);
    }

    setFilteredSongs(filtered);
  };

  // 필터 조건이 변경될 때마다 곡 필터링
  useEffect(() => {
    filterSongs();
  }, [selectedStateSort, selectedKeySort, selectedTheme, selectedTempoSort, allSongs]);

  // 곡수 변경 시 빈 조건 배열을 생성하고 2단계로 진행 (AI 적용 경로(applyAiConti)와 분리되어야 하므로 select onChange에서 직접 호출)
  const handleSongCountChange = (count: number) => {
    setSongCount(count);
    const conditions = Array(count).fill(null).map(() => ({
      stateSort: '',
      theme: '',
      tempoSort: ''
    }));
    setSongConditions(conditions);
    setStep(2);
  };

  // AI가 파싱한 songCount/조건을 적용 (songCount 미지정 시 현재 값 유지, 조건 배열은 songCount 길이에 맞춤)
  // 미지정 필드는 곧바로 랜덤으로 채우고, 조건 리스트 표시와 콘티 생성을 한 번에 진행한다 (completeStep2와 동일한 규칙 재사용)
  const applyAiConti = (result: { songCount: number | null; conditions: SongCondition[] }) => {
    const finalCount = result.songCount ?? songCount;
    const paddedConditions = Array(finalCount).fill(null).map((_, index) => (
      result.conditions[index] ?? { stateSort: '', theme: '', tempoSort: '' }
    ));
    const filledConditions = fillRandomConditions(paddedConditions);

    setSongCount(finalCount);
    setSongConditions(filledConditions);
    setStep(2);
    generateConti(filledConditions);
  };

  const handleAiGenerate = async () => {
    const result = await contiAi.handleGenerate(stateSortOptions, tempoSortOptions);
    if (result) {
      applyAiConti(result);
    }
  };

  // 선택하지 않은 조건들을 랜덤으로 채우기
  const fillRandomConditions = (conditions: SongCondition[]): SongCondition[] => (
    conditions.map(condition => ({
      stateSort: condition.stateSort || getRandomOption(stateSortOptions),
      theme: condition.theme || getRandomOption(themeOptions),
      tempoSort: condition.tempoSort || getRandomOption(tempoSortOptions)
    }))
  );

  // 두 번째 단계 완료 (세부 조건 선택)
  const completeStep2 = () => {
    const updatedConditions = fillRandomConditions(songConditions);
    setSongConditions(updatedConditions);
    generateConti(updatedConditions);
  };

  // 랜덤 옵션 선택 함수
  const getRandomOption = (options: string[]): string => {
    if (options.length === 0) return '';
    const randomIndex = Math.floor(Math.random() * options.length);
    return options[randomIndex];
  };

  // 조건에 맞는 곡이 없을 때 완화할 필드 순서 (템포 → 주제 → 구분 순으로 하나씩 조건을 내려놓는다)
  const CONDITION_RELAX_ORDER: Array<keyof SongCondition> = ['tempoSort', 'theme', 'stateSort'];

  const filterByCondition = (songs: SongData[], condition: SongCondition, activeFields: Array<keyof SongCondition>) => (
    songs.filter(song => activeFields.every((field) => {
      const value = condition[field];
      if (!value) return true;
      if (field === 'theme') return !!song.theme && song.theme.includes(value);
      return song[field] === value;
    }))
  );

  // 조건에 맞는 곡이 없으면 조건을 하나씩 완화해서 다시 찾는다 (곡 풀 자체가 소진된 경우에만 null)
  const pickSongForCondition = (condition: SongCondition, excludeIds: number[]): SongData | null => {
    const pool = allSongs.filter(song => !excludeIds.includes(song.id));
    let activeFields: Array<keyof SongCondition> = ['stateSort', 'theme', 'tempoSort'];
    let candidates = filterByCondition(pool, condition, activeFields);

    for (const field of CONDITION_RELAX_ORDER) {
      if (candidates.length > 0) break;
      activeFields = activeFields.filter(f => f !== field);
      candidates = filterByCondition(pool, condition, activeFields);
    }

    if (candidates.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * candidates.length);
    return candidates[randomIndex];
  };

  // 자동 콘티 생성
  const generateConti = (conditions: SongCondition[]) => {
    const selected: SongData[] = [];

    // 각 곡의 조건에 맞는 곡을 선택 (조건에 맞는 곡이 없으면 조건을 완화해서라도 채운다)
    for (let i = 0; i < conditions.length; i++) {
      const song = pickSongForCondition(conditions[i], selected.map(s => s.id));
      if (!song) break; // 남은 곡 풀 자체가 소진된 경우(곡 수 부족)에만 발생
      selected.push(song);
    }

    setSelectedSongs(selected);
    setIsGenerated(true);
  };

  // 특정 곡만 재생성 (동일 조건 내에서 다른 곡으로 변경, 조건에 맞는 곡이 없으면 조건을 완화)
  const regenerateSong = (songIndex: number) => {
    const condition = songConditions[songIndex];
    const currentSongId = selectedSongs[songIndex].id;
    const excludeIds = selectedSongs
      .filter((_, idx) => idx !== songIndex)
      .map(song => song.id)
      .concat(currentSongId);

    const newSong = pickSongForCondition(condition, excludeIds);
    if (!newSong) return; // 대체할 다른 곡이 없으면 그대로 둔다

    const updatedSongs = [...selectedSongs];
    updatedSongs[songIndex] = newSong;
    setSelectedSongs(updatedSongs);
  };

  // 초기화
  const resetConti = () => {
    setSelectedStateSort('');
    setSelectedKeySort('');
    setSelectedTheme('');
    setSelectedTempoSort('');
    setSelectedSongs([]);
    setIsGenerated(false);
    // songConditions의 모든 값 초기화
    const resetConditions = songConditions.map(condition => ({
      stateSort: '',
      theme: '',
      tempoSort: ''
    }));
    setSongConditions(resetConditions);
  };

  // 조건 업데이트
  const updateSongCondition = (index: number, field: string, value: string) => {
    const newConditions = [...songConditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setSongConditions(newConditions);
  };


  return (
    <div className="conti-maker">
      <div className="conti-maker__inner">
        {/* 섹션 헤더 (ContiMain / 5.html 스타일) */}
        <header className="conti-maker__header">
          {/* <div className="conti-maker__title-bar" /> */}
          <h1 className="conti-maker__title">찬양 콘티 만들기</h1>
          <p className="conti-maker__subtitle">곡 수와 조건을 선택한 뒤 콘티 작성 버튼으로 완성하세요</p>
        </header>

        {isLoading ? (
          <div className="conti-maker__loading">
            <p>곡을 불러오는 중...</p>
          </div>
        ) : (
          <div className="conti-maker__main">
            {/* 왼쪽: 프롬프트 + 상세 설정 */}
            <div className="conti-maker__left-col">
              <section className="conti-maker__ai">
                <div className="conti-maker__ai-head">
                  <div className="conti-maker__ai-head-title">
                    <span className="conti-maker__dot" />
                    <label htmlFor="conti-ai-text">AI 콘티 프롬프트</label>
                  </div>
                  <span className="conti-maker__pill">핵심 입력</span>
                </div>
                <textarea
                  id="conti-ai-text"
                  className="conti-maker__ai-textarea"
                  placeholder="예) 부활절 예배, 6곡, 신나는 곡 위주로"
                  value={contiAi.aiText}
                  onChange={(e) => contiAi.setAiText(e.target.value)}
                  readOnly={!contiAi.isLogin}
                  onFocus={(e) => {
                    if (!contiAi.isLogin) {
                      alert('로그인이 필요합니다.');
                      e.target.blur();
                    }
                  }}
                  onClick={() => {
                    if (!contiAi.isLogin) alert('로그인이 필요합니다.');
                  }}
                />
                <div className="conti-maker__ai-actions">
                  <button
                    type="button"
                    className="conti-maker__btn conti-maker__btn--primary"
                    onClick={handleAiGenerate}
                    disabled={contiAi.isGenerating}
                  >
                    <span className="conti-maker__btn-icon"><MdAutoAwesome size={13} /></span>
                    {contiAi.isGenerating ? '생성 중...' : 'AI로 콘티 만들기'}
                  </button>
                </div>
                {contiAi.errorMessage && (
                  <p className="conti-maker__ai-error">{contiAi.errorMessage}</p>
                )}
              </section>

              <section className="conti-maker__setup">
                <div className="conti-maker__card-head">
                  <div className="conti-maker__card-head-title">
                    <span className="conti-maker__dot" />
                    상세 설정
                  </div>
                  <span className="conti-maker__pill">{songCount}곡</span>
                </div>
                <div className="conti-maker__search">
                  <label className="conti-maker__label">곡 개수</label>
                  <select
                    className="conti-maker__select"
                    value={songCount}
                    onChange={(e) => handleSongCountChange(Number(e.target.value))}
                  >
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((count) => (
                      <option key={count} value={count}>{count}곡</option>
                    ))}
                  </select>
                </div>
                {step >= 2 && (
                  <>
                    <div className="conti-maker__conditions">
                      {songConditions.map((condition, index) => (
                        <div key={index} className="conti-maker__condition-row">
                          <div className="conti-maker__condition-row-head">
                            <span className="conti-maker__condition-badge">{index + 1}</span>
                            <span className="conti-maker__condition-num">{index + 1}번째</span>
                          </div>
                          <div className="conti-maker__condition-selects">
                            <select
                              className="conti-maker__select conti-maker__select--cond"
                              value={condition.stateSort}
                              onChange={(e) => updateSongCondition(index, 'stateSort', e.target.value)}
                            >
                              <option value="">구분</option>
                              {stateSortOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                            <select
                              className="conti-maker__select conti-maker__select--cond"
                              value={condition.theme}
                              onChange={(e) => updateSongCondition(index, 'theme', e.target.value)}
                            >
                              <option value="">주제</option>
                              {themeOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                            <select
                              className="conti-maker__select conti-maker__select--cond"
                              value={condition.tempoSort}
                              onChange={(e) => updateSongCondition(index, 'tempoSort', e.target.value)}
                            >
                              <option value="">템포</option>
                              {tempoSortOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="conti-maker__notice">
                      <span className="conti-maker__notice-label">TIP</span>
                      <p>선택하지 않은 항목은 자동으로 랜덤 선택됩니다.</p>
                    </div>
                    <div className="conti-maker__actions">
                      <button type="button" className="conti-maker__btn conti-maker__btn--primary" onClick={completeStep2}>
                        <span className="conti-maker__btn-icon"><MdAutoAwesome size={13} /></span>
                        콘티 작성
                      </button>
                      <button type="button" className="conti-maker__btn conti-maker__btn--secondary" onClick={resetConti}>
                        초기화
                      </button>
                    </div>
                  </>
                )}
              </section>
            </div>

            {/* 오른쪽: 생성된 콘티 결과 */}
            <div className="conti-maker__right-col">
              <div className="conti-maker__result-header">
                <div className="conti-maker__result-head-row">
                  <div className="conti-maker__result-title-wrap">
                    <span className="conti-maker__dot" />
                    <h2 className="conti-maker__result-title">콘티 (악보 포함)</h2>
                  </div>
                  {isGenerated && selectedSongs.length > 0 && (
                    <span className="conti-maker__pill conti-maker__pill--ok">완성</span>
                  )}
                </div>
                <p className="conti-maker__result-subtitle">악보, 가사, 멘트 가이드까지 완벽하게 통합된 콘티</p>
              </div>

              {isGenerated && selectedSongs.length > 0 ? (
                <div className="conti-maker__result-body">
                  <div className="conti-maker__paper-wrap">
                    <div className="conti-maker__paper conti-maker__paper--back" />
                    <div className="conti-maker__paper conti-maker__paper--front" ref={contiGridRef}>
                      <div className="conti-maker__paper-header">
                        {!isMobile && (
                          <div className="conti-maker__paper-options">
                            <div className="conti-maker__paper-option">
                              <label>용지</label>
                              <select value={paperSize} onChange={(e) => setPaperSize(e.target.value)} className="conti-maker__select conti-maker__select--sm">
                                <option value="A4 가로">A4 가로</option>
                                <option value="A4 세로">A4 세로</option>
                              </select>
                            </div>
                            <div className="conti-maker__paper-option">
                              <label>한 줄 곡 수</label>
                              <select value={songsPerRow} onChange={(e) => setSongsPerRow(Number(e.target.value))} className="conti-maker__select conti-maker__select--sm">
                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                  <option key={n} value={n}>{n}곡</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                      <div
                        className="conti-maker__songs-grid"
                        style={{
                          gridTemplateColumns: `repeat(${isMobile ? 1 : songsPerRow}, 1fr)`,
                        }}
                      >
                        {selectedSongs.map((song, index) => (
                          <div
                            key={song.id}
                            className="conti-maker__song-card"
                            onClick={() => {
                              const url = `/aiservice/worship/detail?id=${song.id}`;
                              window.open(url, '_blank');
                            }}
                          >
                            <div className="conti-maker__song-card-head">
                              <div className="conti-maker__song-card-title-wrap">
                                <span className="conti-maker__song-label">CONTI · {index + 1 < 10 ? `0${index + 1}` : index + 1}</span>
                                <span className="conti-maker__song-card-title">{song.title}</span>
                              </div>
                              <span className="conti-maker__song-pill">{song.keySort} | {song.tempoSort}</span>
                            </div>
                            <div className="conti-maker__song-card-actions">
                              <button
                                type="button"
                                className="conti-maker__regenerate-btn"
                                onClick={(e) => { e.stopPropagation(); regenerateSong(index); }}
                              >
                                <MdAutorenew size={13} />
                                변경
                              </button>
                            </div>
                            <div className="conti-maker__song-score">
                              {song.image ? (
                                <img
                                  src={song.image}
                                  alt={song.title}
                                  onError={(e) => {
                                    e.currentTarget.src = altImage;
                                    e.currentTarget.onerror = null;
                                  }}
                                />
                              ) : (
                                <span className="conti-maker__no-score">악보 없음</span>
                              )}
                            </div>
                            <div className="conti-maker__song-card-tags">
                              <span className="conti-maker__chip-tag">{song.stateSort}</span>
                              {!!song.theme && (
                                <span className="conti-maker__chip-tag conti-maker__chip-tag--dim">{song.theme}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="conti-maker__paper-footer">
                        <p>Generated by PraiseMaker</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="conti-maker__empty-result">
                  <p>곡 수와 조건을 선택한 뒤 콘티 작성 버튼을 누르면<br />이곳에 결과가 표시됩니다.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}